import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Bot, Plus, Copy, UploadCloud, Zap, Sparkles, PlayCircle, Save, RefreshCw,
  Database, BrainCircuit, FileText, Image as ImageIcon, Video, Phone, PhoneCall,
  Calendar, Mail, ShieldAlert, Info, Layers, Link as LinkIcon, UserCheck,
} from 'lucide-react';
import FaqMediaFields from '@/components/FaqMediaFields';
import { useAuth } from '@/contexts/AuthContext';
import { SuperadminAICompanySelector } from '@/components/SuperadminAICompanySelector';
import { Navigate } from 'react-router-dom';

// Mapa de iconos por skill key (catálogo /api/ai/skills-catalog).
const SKILL_ICONS = {
  enviar_ficha: FileText,
  enviar_brochure: FileText,
  enviar_imagenes: ImageIcon,
  enviar_video: Video,
  enviar_url: LinkIcon,
  enviar_media_faq: Layers,
  enviar_plano: FileText,
  iniciar_llamada: PhoneCall,
  programar_llamada: Phone,
  agendar_cita: Calendar,
  proponer_cita: Calendar,
  cambiar_intent: RefreshCw,
  capturar_email: Mail,
  capturar_nombre: UserCheck,
  no_contactar: ShieldAlert,
};

const OPENROUTER_DEFAULT_MODELS = ['openai/gpt-4.1-nano', 'gemini/gemini-3-flash-preview'];

const makeInitialForm = () => ({
  id: '',
  name: '',
  is_active: true,
  models: [...OPENROUTER_DEFAULT_MODELS],
  primary_model: OPENROUTER_DEFAULT_MODELS[0],
  behavior_prompt: 'Eres un experto comercial consultivo. Califica, orienta y lleva al prospecto al siguiente paso con claridad.',
  temperature: 0.7,
  knowledge_text: '',
  knowledge_files: [],
  inherit_company_knowledge: true,
  active_skills: [],
  faqs: [{ question: '', answer: '', trigger: '', skill: '', images: [], pdfs: [], videos: [] }],
  trigger_channels: ['whatsapp', 'web_form'],
  default_campaign_message: 'Hola, vi tu interés. Soy tu asistente IA y puedo ayudarte con opciones, costos y siguiente paso.',
  send_auto_whatsapp: true,
  send_auto_email: false,
  webhook_url: '',
  campaign_ids_attended: [],
  development_id: '',
  brand_id: '',
});

export default function AICampaignIndependentPage({ embedded = false } = {}) {
  const { user, impersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [brands, setBrands] = useState([]);
  const [wabas, setWabas] = useState([]);
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState('');
  const [models, setModels] = useState([]);
  const [skillsCatalog, setSkillsCatalog] = useState([]);
  const [form, setForm] = useState(makeInitialForm);

  // Superadmin sin impersonar → company_id = 'dealider-system' (sin tenant real).
  const isSuperadminNoTenant = !impersonating
    && user?.role === 'superadmin'
    && (user?.company_id === 'dealider-system' || user?.platform_role === 'owner' || user?.platform_role === 'superadmin');
  const [simulatePrompt, setSimulatePrompt] = useState('Quiero saber opciones y formas de pago para avanzar esta semana.');
  const [simulateReply, setSimulateReply] = useState('');
  const [simulating, setSimulating] = useState(false);

  const selectedCampaign = useMemo(() => campaigns.find((c) => c.id === form.id), [campaigns, form.id]);

  const hydrateForm = (campaign) => {
    const base = makeInitialForm();
    setForm({
      ...base,
      ...campaign,
      inherit_company_knowledge: !!campaign.inherit_company_knowledge,
      active_skills: Array.isArray(campaign.active_skills) ? campaign.active_skills : [],
      faqs: (campaign.faqs && campaign.faqs.length)
        ? campaign.faqs.map((f) => ({
            id: f.id || '',
            question: f.question || '',
            answer: f.answer || '',
            trigger: f.trigger || '',
            skill: f.skill || '',
            images: f.images || [],
            pdfs: f.pdfs || [],
            videos: f.videos || [],
          }))
        : [{ question: '', answer: '', trigger: '', skill: '', images: [], pdfs: [], videos: [] }],
      models: (campaign.models && campaign.models.length) ? campaign.models : base.models,
      trigger_channels: (campaign.trigger_channels && campaign.trigger_channels.length) ? campaign.trigger_channels : base.trigger_channels,
      campaign_ids_attended: Array.isArray(campaign.campaign_ids_attended) ? campaign.campaign_ids_attended : [],
      development_id: campaign.development_id || '',
      brand_id: campaign.brand_id || '',
    });
    setSimulateReply('');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [campaignRes, modelRes, crmRes, skillsRes, devRes, wabaRes, prodRes, brandRes] = await Promise.all([
        api.get('/ai-independent-campaigns'),
        api.get('/ai-independent-campaigns/models'),
        api.get('/campaigns'),
        api.get('/ai/skills-catalog'),
        api.get('/developments').catch(() => ({ data: [] })),
        api.get('/waba-accounts').catch(() => ({ data: [] })),
        api.get('/products').catch(() => ({ data: [] })),
        api.get('/brands').catch(() => ({ data: [] })),
      ]);
      const campaignList = campaignRes.data || [];
      setCampaigns(campaignList);
      setModels(modelRes.data || []);
      setCrmCampaigns(crmRes.data || []);
      setSkillsCatalog(skillsRes.data?.skills || []);
      setDevelopments(Array.isArray(devRes.data) ? devRes.data : []);
      setWabas(Array.isArray(wabaRes.data) ? wabaRes.data : (wabaRes.data?.items || []));
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
      setBrands(Array.isArray(brandRes.data) ? brandRes.data : []);
      if (campaignList.length) {
        hydrateForm(campaignList[0]);
      } else {
        setForm(makeInitialForm());
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron cargar las campañas IA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      // Superadmin sin impersonar: no cargamos datos de tenant (no hay compañía
      // seleccionada). Mostramos el selector de compañía en su lugar.
      if (isSuperadminNoTenant) { setLoading(false); return; }
      await loadData();
    })();
  }, []);

  const resetNew = () => {
    setForm(makeInitialForm());
    setSimulateReply('');
  };

  const toggleChannel = (channel) => {
    const has = form.trigger_channels.includes(channel);
    const next = has ? form.trigger_channels.filter((c) => c !== channel) : [...form.trigger_channels, channel];
    if (!next.length) {
      toast.error('Selecciona al menos un canal activo');
      return;
    }
    setForm((prev) => ({ ...prev, trigger_channels: next }));
  };

  const addFaq = () => setForm((prev) => ({ ...prev, faqs: [...prev.faqs, { question: '', answer: '', trigger: '', skill: '', images: [], pdfs: [], videos: [] }] }));
  const removeFaq = (idx) => setForm((prev) => ({ ...prev, faqs: prev.faqs.filter((_, i) => i !== idx) }));

  // Quita un archivo de la base de conocimiento local: filtra knowledge_files[] y,
  // si ese archivo anexó su contenido a knowledge_text (bloque "### Archivo: <name>"),
  // recorta ESE bloque (hasta el siguiente "### Archivo:" o EOF) sin tocar el texto
  // tecleado ni otros bloques. PDF → no hay header → solo filtra el array.
  // Caso patológico aceptado: si el usuario tecleó "### Archivo: <nombre>" a mano
  // coincidiendo con un archivo real, podría recortar de más (baja prob., no se blinda).
  const removeKnowledgeFile = (idx) => {
    const target = (form.knowledge_files || [])[idx];
    if (!target) return;
    if (!window.confirm(`¿Quitar "${target.name}" de la base de conocimiento? La IA dejará de usar su contenido. Recuerda Guardar para aplicar.`)) return;
    setForm((prev) => {
      const nextFiles = (prev.knowledge_files || []).filter((_, i) => i !== idx);
      let nextText = prev.knowledge_text || '';
      const header = `### Archivo: ${target.name}`;
      const start = nextText.indexOf(header);
      if (start !== -1) {
        const blockStart = nextText.slice(start - 2, start) === '\n\n' ? start - 2 : start;
        const nextHeader = nextText.indexOf('\n\n### Archivo:', start + header.length);
        const blockEnd = nextHeader === -1 ? nextText.length : nextHeader;
        nextText = (nextText.slice(0, blockStart) + nextText.slice(blockEnd)).trim();
      }
      return { ...prev, knowledge_files: nextFiles, knowledge_text: nextText };
    });
  };

  const toggleSkill = (skillKey) => setForm((prev) => {
    const current = prev.active_skills || [];
    const next = current.includes(skillKey) ? current.filter((k) => k !== skillKey) : [...current, skillKey];
    return { ...prev, active_skills: next };
  });

  const activeSkillsCount = (form.active_skills || []).length;
  const activeSkillObjects = useMemo(
    () => skillsCatalog.filter((s) => (form.active_skills || []).includes(s.key)),
    [skillsCatalog, form.active_skills],
  );

  const attendsAll = (form.campaign_ids_attended || []).includes('all');
  const btype = user?.business_type || user?.company?.business_type || '';
  const isDevVertical = btype === 'developer' || btype === 'franchise';
  const crmByDev = form.development_id
    ? crmCampaigns.filter((c) => !c.development_id || c.development_id === form.development_id)
    : crmCampaigns;
  const crmVisible = productFilter
    ? crmByDev.filter((c) => c.product_id === productFilter)
    : crmByDev;
  const wabaForDev = (wabas || []).find((w) =>
    (w.scope?.type === 'development' && w.scope?.ref_id === form.development_id) ||
    w.development_id === form.development_id
  );
  const setAttendAll = (value) => setForm((prev) => ({ ...prev, campaign_ids_attended: value ? ['all'] : [] }));
  const toggleAttendCampaign = (campaignId) => setForm((prev) => {
    const current = (prev.campaign_ids_attended || []).filter((id) => id !== 'all');
    const next = current.includes(campaignId) ? current.filter((id) => id !== campaignId) : [...current, campaignId];
    return { ...prev, campaign_ids_attended: next };
  });

  const updateFaq = (idx, field, value) => {
    setForm((prev) => {
      const next = [...prev.faqs];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, faqs: next };
    });
  };
  const replaceFaq = (idx, nextFaq) => {
    setForm((prev) => {
      const next = [...prev.faqs];
      next[idx] = nextFaq;
      return { ...prev, faqs: next };
    });
  };

  const onKnowledgeFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const fileMeta = list.map((f) => ({ name: f.name, size: f.size, type: f.type || 'application/octet-stream' }));

    let appendedKnowledge = '';
    for (const file of list.slice(0, 5)) {
      const isTextLike = /text|json|csv|xml|javascript|markdown/.test(file.type || '') || /\.(txt|md|json|csv|xml|js)$/i.test(file.name);
      if (!isTextLike || file.size > 1024 * 1024) continue;
      try {
        const content = await file.text();
        appendedKnowledge += `\n\n### Archivo: ${file.name}\n${content.slice(0, 8000)}`;
      } catch {
        // ignore unreadable file
      }
    }

    setForm((prev) => ({
      ...prev,
      knowledge_files: [...(prev.knowledge_files || []), ...fileMeta],
      knowledge_text: `${prev.knowledge_text || ''}${appendedKnowledge}`.trim(),
    }));
    toast.success('Archivos agregados a la base de conocimiento');
  };

  const saveCampaign = async () => {
    if (!form.name?.trim()) {
      toast.error('Nombre de campaña requerido');
      return;
    }
    setSaving(true);
    try {
      const models = (form.models && form.models.length) ? form.models : OPENROUTER_DEFAULT_MODELS;
      const payload = {
        ...form,
        models,
        primary_model: form.primary_model && models.includes(form.primary_model)
          ? form.primary_model
          : models[0],
        active_skills: form.active_skills || [],
        inherit_company_knowledge: !!form.inherit_company_knowledge,
        faqs: (form.faqs || [])
          .filter((f) => f.question?.trim() && f.answer?.trim())
          .map((f) => ({
            question: f.question.trim(),
            answer: f.answer.trim(),
            ...(f.id ? { id: f.id } : {}),
            images: f.images || [],
            pdfs: f.pdfs || [],
            videos: f.videos || [],
            ...(f.skill ? { skill: f.skill, trigger: (f.trigger || '').trim() } : {}),
          })),
      };

      if (form.id) {
        await api.put(`/ai-independent-campaigns/${form.id}`, payload);
      } else {
        await api.post('/ai-independent-campaigns', payload);
      }
      toast.success('Campaña IA guardada correctamente');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la campaña');
    } finally {
      setSaving(false);
    }
  };

  const toggleCampaign = async (campaign, value) => {
    try {
      await api.post(`/ai-independent-campaigns/${campaign.id}/toggle`, { is_active: value });
      setCampaigns((prev) => prev.map((c) => (c.id === campaign.id ? { ...c, is_active: value } : c)));
      if (form.id === campaign.id) {
        setForm((prev) => ({ ...prev, is_active: value }));
      }
      toast.success(value ? 'Campaña activada' : 'Campaña desactivada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar el estado');
    }
  };

  const copyWebhook = async () => {
    if (!form.webhook_url) {
      toast.error('Guarda la campaña para generar webhook');
      return;
    }
    try {
      await navigator.clipboard.writeText(form.webhook_url);
      toast.success('Webhook copiado');
    } catch {
      toast.error('No se pudo copiar el webhook');
    }
  };

  const simulate = async () => {
    if (!form.id) {
      toast.error('Primero guarda la campaña');
      return;
    }
    setSimulating(true);
    try {
      const response = await api.post(`/ai-independent-campaigns/${form.id}/simulate`, {
        name: 'Lead Demo',
        email: 'lead.demo@example.com',
        phone: '5215511111111',
        message: simulatePrompt,
      });
      setSimulateReply(response.data?.reply || '');
      toast.success(`Simulación generada con ${response.data?.used_model || 'modelo IA'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo simular respuesta IA');
    } finally {
      setSimulating(false);
    }
  };

  if (!embedded && !isSuperadminNoTenant) {
    return <Navigate to="/settings?tab=ia&sub=campanas" replace />;
  }

  if (loading) {
    return (
      <div className="space-y-4" data-testid="ai-independent-campaigns-loading">
        <Card className="border-white/10 bg-black/30"><CardContent className="p-6 text-sm text-white/60">Cargando campañas IA...</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-independent-campaigns-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className={`${embedded ? 'text-lg' : 'text-2xl'} font-heading font-semibold flex items-center gap-2`} data-testid="ai-independent-campaigns-title">
            <Sparkles className="w-5 h-5 text-violet-400" /> Campañas de este asistente
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="ai-independent-campaigns-subtitle">
            Por defecto cada campana usa el Asistente de tu empresa y el conocimiento de este producto o desarrollo. Si quieres, dale un cerebro especializado: su propio comportamiento y guion de venta para ese producto. El modelo de IA y la voz siempre se heredan de tu empresa.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData} data-testid="ai-independent-refresh-button"><RefreshCw className="w-4 h-4 mr-1" />Actualizar</Button>
          <Button onClick={resetNew} data-testid="ai-independent-new-button"><Plus className="w-4 h-4 mr-1" />Nueva campaña</Button>
        </div>
      </div>

      {isSuperadminNoTenant && (
        <SuperadminAICompanySelector targetLabel="Campañas IA" />
      )}

      {!isSuperadminNoTenant && (
      <div className="grid xl:grid-cols-[360px_1fr] gap-5">
        <Card className="border-white/10 bg-black/20" data-testid="ai-independent-campaign-list-card">
          <CardHeader className="pb-3"><CardTitle className="text-sm">Campañas configuradas ({campaigns.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[72vh] overflow-y-auto">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                role="button"
                tabIndex={0}
                onClick={() => hydrateForm(campaign)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hydrateForm(campaign); } }}
                className={`w-full text-left rounded-xl border p-3 transition-colors cursor-pointer ${form.id === campaign.id ? 'border-violet-500/40 bg-violet-500/10' : 'border-white/10 bg-black/20 hover:border-white/20'}`}
                data-testid={`ai-independent-campaign-item-${campaign.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-white truncate">{campaign.name}</p>
                  <Switch
                    checked={!!campaign.is_active}
                    onCheckedChange={(value) => toggleCampaign(campaign, value)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`ai-independent-toggle-${campaign.id}`}
                  />
                </div>
                <div className="mt-2 flex gap-2 flex-wrap text-[10px] text-white/60">
                  <Badge variant="outline" className="border-white/10">Hits: {campaign.stats?.webhook_hits || 0}</Badge>
                  <Badge variant="outline" className="border-white/10">Leads: {campaign.stats?.leads_created || 0}</Badge>
                  <Badge variant="outline" className="border-white/10">IA: {campaign.stats?.ai_replies || 0}</Badge>
                </div>
              </div>
            ))}
            {!campaigns.length && <p className="text-xs text-muted-foreground" data-testid="ai-independent-empty-state">Aún no tienes campañas IA independientes.</p>}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-violet-500/20 bg-[#0d0b16]" data-testid="ai-independent-editor-card">
            <CardContent className="p-5 space-y-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-lg font-semibold text-violet-300 flex items-center gap-2" data-testid="ai-independent-editor-title"><Bot className="w-5 h-5" /> Nuevo Agente IA</p>
                <Badge className="bg-violet-500/20 text-violet-200 border-violet-400/20" data-testid="ai-independent-status-badge">{form.is_active ? 'Activo' : 'Inactivo'}</Badge>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-violet-200">Nombre de campaña</Label>
                <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Ej. Captación Premium Polanco" data-testid="ai-independent-name-input" />
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-3 space-y-1" data-testid="ai-independent-openrouter-note">
                <p className="text-xs font-semibold text-white">Modelo: lo elige LeadCEM (OpenRouter)</p>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Aquí no se escoge GPT ni Gemini. Usa el mismo motor barato y con failover que el Asistente.
                  El equipo de LeadCEM lo ajusta en Super Admin → Uso de IA.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-violet-200">PROMPT DE COMPORTAMIENTO</Label>
                <Textarea
                  value={form.behavior_prompt}
                  onChange={(e) => setForm((prev) => ({ ...prev, behavior_prompt: e.target.value }))}
                  placeholder="Describe cómo debe actuar tu agente..."
                  rows={4}
                  data-testid="ai-independent-behavior-prompt-input"
                />
              </div>

              <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4 space-y-3" data-testid="ai-independent-knowledge-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <Database className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-sky-200">Heredar Base de Conocimiento de la empresa</p>
                      <p className="text-[11px] text-sky-200/70 mt-0.5">
                        Usa las FAQs y el conocimiento del Agente Principal como voz de marca.
                      El prompt de esta campaña es lo propio del desarrollo.
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={!!form.inherit_company_knowledge}
                    onCheckedChange={(v) => setForm((prev) => ({ ...prev, inherit_company_knowledge: v }))}
                    aria-label="Heredar base de conocimiento de la empresa"
                    data-testid="ai-independent-knowledge-inherit-toggle"
                  />
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid="ai-independent-knowledge-inherit-note">
                  <Info className="w-3.5 h-3.5 text-sky-300/80 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-white/60">
                    La gestión principal de documentos y auto-index ahora vive dentro de la configuración del Agente Principal.
                    Este bloque permite overrides o uso independiente de la campaña.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-violet-200">BASE DE CONOCIMIENTO (OVERRIDE LOCAL)</Label>
                <div className="rounded-xl border border-dashed border-violet-500/30 bg-violet-500/5 p-4" data-testid="ai-independent-knowledge-dropzone">
                  <label className="cursor-pointer flex flex-col items-center gap-2 text-center">
                    <UploadCloud className="w-6 h-6 text-violet-300" />
                    <p className="text-xs text-violet-100">Documentos, fotos o textos de negocio</p>
                    <p className="text-[10px] text-violet-200/70">Arrastra o haz clic para cargar archivos.</p>
                    <input type="file" multiple className="hidden" onChange={(e) => onKnowledgeFiles(e.target.files)} data-testid="ai-independent-knowledge-file-input" />
                  </label>
                </div>
                <Textarea value={form.knowledge_text} onChange={(e) => setForm((prev) => ({ ...prev, knowledge_text: e.target.value }))} rows={4} placeholder="Contexto, catálogo, precios, políticas, etc..." data-testid="ai-independent-knowledge-text-input" />
                {!!form.knowledge_files?.length && (
                  <div className="flex flex-wrap gap-1" data-testid="ai-independent-knowledge-files-list">
                    {form.knowledge_files.map((f, idx) => (
                      <Badge key={`${f.name}-${idx}`} variant="outline" className="border-white/10 text-[10px] gap-1">
                        {f.name}
                        <button
                          type="button"
                          onClick={() => removeKnowledgeFile(idx)}
                          className="ml-0.5 text-zinc-400 hover:text-red-400 leading-none"
                          aria-label={`Quitar ${f.name}`}
                          data-testid={`ai-independent-knowledge-file-remove-${idx}`}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-2" data-testid="ai-independent-skills-section">
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  <BrainCircuit className="w-4 h-4 text-sky-300" /> Habilidades
                </p>
                <p className="text-[11px] text-white/60 leading-relaxed">
                  Las habilidades (ficha, cita, llamada) se configuran una sola vez en
                  Configuración → Asistente IA. Esta campaña usa las mismas. No se eligen otra vez aquí.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-violet-200">FAQS FRECUENTES</Label>
                  <Button size="sm" variant="outline" onClick={addFaq} data-testid="ai-independent-add-faq-button"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
                </div>
                <div className="space-y-2" data-testid="ai-independent-faq-list">
                  {(form.faqs || []).map((faq, idx) => (
                    <div key={idx} className="rounded-xl border border-white/10 p-3 bg-black/20 space-y-2" data-testid={`ai-independent-faq-item-${idx}`}>
                      <Input value={faq.question || ''} onChange={(e) => updateFaq(idx, 'question', e.target.value)} placeholder="P: ¿Cuál es el proceso?" data-testid={`ai-independent-faq-question-${idx}`} />
                      <Textarea value={faq.answer || ''} onChange={(e) => updateFaq(idx, 'answer', e.target.value)} rows={2} placeholder="R: Te guiamos paso a paso..." data-testid={`ai-independent-faq-answer-${idx}`} />
                      <FaqMediaFields faq={faq} onChange={(next) => replaceFaq(idx, next)} testId={`ai-independent-faq-media-${idx}`} />
                      <div className="border-t border-white/5 mt-3 pt-3 space-y-2">
                        <p className="text-[10px] text-white/45 flex items-center gap-1">
                          <Zap className="w-3 h-3 text-sky-300/80" /> Gatillo opcional → activa una habilidad cuando se detecta esta intención.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-2">
                          <Input
                            value={faq.trigger || ''}
                            onChange={(e) => updateFaq(idx, 'trigger', e.target.value)}
                            placeholder="Gatillo / Intención (ej. Quiero agendar)"
                            data-testid={`ai-independent-faq-intent-input-${idx}`}
                          />
                          <Select
                            value={faq.skill || 'none'}
                            onValueChange={(value) => updateFaq(idx, 'skill', value === 'none' ? '' : value)}
                          >
                            <SelectTrigger data-testid={`ai-independent-faq-skill-select-${idx}`}>
                              <SelectValue placeholder="Habilidad a activar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ninguna</SelectItem>
                              {(activeSkillObjects.length ? activeSkillObjects : skillsCatalog).map((s) => (
                                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {(form.faqs || []).length > 1 && <Button size="sm" variant="ghost" onClick={() => removeFaq(idx)} data-testid={`ai-independent-remove-faq-${idx}`}>Eliminar</Button>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-3" data-testid="ai-independent-trigger-card">
                <p className="text-sm font-semibold text-emerald-300 flex items-center gap-2"><Zap className="w-4 h-4" /> Trigger Automático</p>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  {['whatsapp', 'web_form', 'email'].map((channel) => {
                    const active = form.trigger_channels.includes(channel);
                    return (
                      <button
                        key={channel}
                        type="button"
                        onClick={() => toggleChannel(channel)}
                        className={`px-2.5 py-1 rounded-full border text-xs ${active ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-100' : 'bg-black/20 border-white/10 text-white/60'}`}
                        data-testid={`ai-independent-channel-${channel}`}
                      >
                        {channel === 'web_form' ? 'Web Form' : channel.charAt(0).toUpperCase() + channel.slice(1)}
                      </button>
                    );
                  })}
                </div>
                <Textarea
                  value={form.default_campaign_message}
                  onChange={(e) => setForm((prev) => ({ ...prev, default_campaign_message: e.target.value }))}
                  rows={3}
                  data-testid="ai-independent-default-message-input"
                />
                <div className="flex items-center gap-6 text-xs">
                  <label className="flex items-center gap-2" data-testid="ai-independent-send-whatsapp-wrap">
                    <Switch checked={!!form.send_auto_whatsapp} onCheckedChange={(v) => setForm((prev) => ({ ...prev, send_auto_whatsapp: v }))} data-testid="ai-independent-send-whatsapp-switch" />
                    Auto WhatsApp
                  </label>
                  <label className="flex items-center gap-2" data-testid="ai-independent-send-email-wrap">
                    <Switch checked={!!form.send_auto_email} onCheckedChange={(v) => setForm((prev) => ({ ...prev, send_auto_email: v }))} data-testid="ai-independent-send-email-switch" />
                    Auto Email
                  </label>
                </div>
              </div>

              {isDevVertical && (
                <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-3" data-testid="ai-independent-development-card">
                  <p className="text-sm font-semibold text-violet-200 flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Desarrollo que atiende este cerebro
                  </p>
                  <p className="text-[11px] text-violet-200/70">
                    Un desarrollo = un WhatsApp + este asistente. La marca (voz) se hereda arriba. Paseos y Bosques no se mezclan.
                  </p>
                  <Select
                    value={form.development_id || 'none'}
                    onValueChange={(v) => setForm((prev) => ({ ...prev, development_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="ai-independent-development">
                      <SelectValue placeholder="Elige el desarrollo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin desarrollo (no recomendado)</SelectItem>
                      {developments.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name || d.title || d.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.development_id && wabaForDev && (
                    <p className="text-[11px] text-emerald-300" data-testid="ai-independent-waba-hint">
                      WhatsApp de este desarrollo: {wabaForDev.name || wabaForDev.phone_number || wabaForDev.id}
                    </p>
                  )}
                  {form.development_id && !wabaForDev && (
                    <p className="text-[11px] text-amber-300">
                      Este desarrollo no tiene WhatsApp propio. Así cae al agente genérico (el caso QUMA).
                    </p>
                  )}
                </div>
              )}

              {!isDevVertical && btype !== 'car_dealer' && brands.length > 0 && (
                <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-3" data-testid="ai-independent-brand-card">
                  <p className="text-sm font-semibold text-violet-200 flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Marca que atiende este cerebro
                  </p>
                  <p className="text-[11px] text-violet-200/70">
                    Ata este cerebro especializado a una marca. En la vista de Marcas se mostrara aqui asociada. La marca (voz) se hereda arriba.
                  </p>
                  <Select
                    value={form.brand_id || 'none'}
                    onValueChange={(v) => setForm((prev) => ({ ...prev, brand_id: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger className="bg-black/20 border-white/10 text-xs" data-testid="ai-independent-brand">
                      <SelectValue placeholder="Elige la marca" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin marca (aplica a todo el tenant)</SelectItem>
                      {brands.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!isDevVertical && btype !== 'car_dealer' && (
                <div className="rounded-xl border border-violet-500/30 bg-violet-500/10 p-4 space-y-2" data-testid="ai-independent-product-filter-card">
                  <p className="text-sm font-semibold text-violet-200 flex items-center gap-2"><Layers className="w-4 h-4" /> Es este asistente para un producto en concreto?</p>
                  <p className="text-[11px] text-violet-200/70">Filtra las campanas por producto para no elegirlas una por una.</p>
                  <Select value={productFilter || 'all'} onValueChange={(v) => setProductFilter(v === 'all' ? '' : v)}>
                    <SelectTrigger className="text-xs" data-testid="ai-independent-product-filter"><SelectValue placeholder="Todos mis productos" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos mis productos</SelectItem>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {productFilter && crmVisible.length > 0 && (
                    <Button size="sm" variant="outline" className="text-[11px]" onClick={() => setForm((prev) => ({ ...prev, campaign_ids_attended: crmVisible.map((c) => c.id) }))} data-testid="ai-independent-product-selectall">
                      Seleccionar todas las de este producto ({crmVisible.length})
                    </Button>
                  )}
                  {productFilter && crmVisible.length === 0 && (
                    <p className="text-[11px] text-amber-300">Este producto aun no tiene campanas. Crealas en su ficha, tab Campanas.</p>
                  )}
                </div>
              )}

              <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4 space-y-3" data-testid="ai-independent-attended-card">
                <div>
                  <p className="text-sm font-semibold text-sky-300 flex items-center gap-2"><Zap className="w-4 h-4" /> Esta configuración de IA atiende las siguientes campañas</p>
                  <p className="text-[11px] text-sky-200/70 mt-1">Define qué campañas responde este agente. Si no seleccionas ninguna, mantiene su comportamiento directo por webhook (legacy).</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-sky-100" data-testid="ai-independent-attend-all-wrap">
                  <Switch checked={attendsAll} onCheckedChange={setAttendAll} data-testid="ai-independent-attend-all-switch" />
                  {isDevVertical && form.development_id ? 'Todas las campañas de ESTE desarrollo' : 'Todas las campañas activas'}
                </label>
                {!attendsAll && (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1" data-testid="ai-independent-attended-list">
                    {crmVisible.length === 0 && (
                      <p className="text-[11px] text-white/50" data-testid="ai-independent-attended-empty">No hay campañas CRM para seleccionar.</p>
                    )}
                    {crmVisible.map((c) => {
                      const checked = (form.campaign_ids_attended || []).includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleAttendCampaign(c.id)}
                          className={`w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors ${checked ? 'border-sky-400/40 bg-sky-500/15 text-sky-100' : 'border-white/10 bg-black/20 text-white/70 hover:border-white/20'}`}
                          data-testid={`ai-independent-attend-campaign-${c.id}`}
                        >
                          <span className="truncate">{c.name || c.id}</span>
                          {checked && <Badge className="bg-sky-500/30 text-sky-100 border-sky-400/30 text-[10px]">Atendida</Badge>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="text-[11px] text-sky-200/80" data-testid="ai-independent-attended-summary">
                  {attendsAll
                    ? 'Atiende: TODAS las campañas activas'
                    : ((form.campaign_ids_attended || []).length
                      ? `Atiende ${(form.campaign_ids_attended || []).length} campaña(s) seleccionada(s)`
                      : 'Sin restricción (comportamiento directo por webhook)')}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="ai-independent-webhook-box">
                <p className="text-xs text-white/70 mb-1">Webhook único de esta campaña</p>
                <div className="flex gap-2">
                  <Input value={form.webhook_url || 'Guarda la campaña para generar webhook'} readOnly data-testid="ai-independent-webhook-input" />
                  <Button variant="outline" onClick={copyWebhook} data-testid="ai-independent-copy-webhook-button"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={saveCampaign} disabled={saving} data-testid="ai-independent-save-button"><Save className="w-4 h-4 mr-1" />{saving ? 'Guardando...' : (form.id ? 'Actualizar campaña IA' : 'Crear campaña IA')}</Button>
                <Button variant="outline" onClick={simulate} disabled={simulating || !form.id} data-testid="ai-independent-simulate-button"><PlayCircle className="w-4 h-4 mr-1" />{simulating ? 'Simulando...' : 'Probar asistente IA'}</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-black/20" data-testid="ai-independent-simulate-card">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Simulación de respuesta IA</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea value={simulatePrompt} onChange={(e) => setSimulatePrompt(e.target.value)} rows={3} data-testid="ai-independent-simulate-prompt-input" />
              <div className="rounded-xl border border-white/10 bg-black/20 p-3 min-h-[96px]" data-testid="ai-independent-simulate-reply-box">
                <p className="text-xs text-white/85 whitespace-pre-wrap">{simulateReply || 'Aquí verás la respuesta generada por tu asistente IA.'}</p>
              </div>
              {selectedCampaign && <p className="text-[10px] text-muted-foreground" data-testid="ai-independent-selected-campaign-note">Campaña seleccionada: {selectedCampaign.name}</p>}
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
